import { ERouteControlViewType, Map as NewbuildingMapComponent } from '@cian/frontend-newbuilding-map-component';
import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { selectGeoCoordinates } from 'shared/selectors';
import { selectNewbuildingDisplayName } from 'shared/selectors/newObject';
import { getIsMapServicesVisible } from 'shared/selectors/newbuildingMap';
import { EMapService, IRoutingBuildSuccessParams, TInfrastructureItem, TTAInfrastructureItem } from 'shared/types/map';

import { useTransportAccessibilityRouting } from './useTransportAccessibilityRouting';
import { trackBuildRouteClick, trackBuildRouteSuccess } from '../analytics';
import { checkIsInfrastructureRoute, isTransportAccessibilityInfrastructureItem } from '../utils';

interface IUseRoutingParams {
  hasFutureInfrastructure: boolean;
  closeOnboarding: () => void;
  mapApi: NewbuildingMapComponent | null;
  regularInfrastructureType: string | undefined;
  setMapService: (mapService: EMapService) => void;
}

export function useRouting({
  hasFutureInfrastructure,
  closeOnboarding,
  mapApi,
  regularInfrastructureType,
  setMapService,
}: IUseRoutingParams) {
  const { setTARouting, resetTARouting } = useTransportAccessibilityRouting(mapApi);

  const infrastructurePointRef = useRef<TInfrastructureItem | TTAInfrastructureItem | null>(null);

  const pinCoordinates = useSelector(selectGeoCoordinates);
  const newbuildingName = useSelector(selectNewbuildingDisplayName);

  const isMapServicesVisible = useSelector(getIsMapServicesVisible);

  const startRouting = useCallback(
    (geoItem?: TInfrastructureItem | TTAInfrastructureItem, routeControlViewType?: ERouteControlViewType) => {
      const from = pinCoordinates
        ? {
            coordinates: pinCoordinates,
            fixed: true,
            label: newbuildingName || undefined,
          }
        : /* istanbul ignore next */
          undefined;

      const to = geoItem ? { coordinates: geoItem.coordinates } : undefined;

      mapApi?.startRouting(
        from,
        to,
        'properties.name === "main" && geometry.type === "Polygon"',
        undefined,
        routeControlViewType,
      );
    },
    [newbuildingName, pinCoordinates, mapApi],
  );

  const startBuildingRoute = useCallback(
    (item?: TInfrastructureItem | TTAInfrastructureItem, routeControlViewType?: ERouteControlViewType) => {
      closeOnboarding();
      startRouting(item, routeControlViewType);
      trackBuildRouteClick(regularInfrastructureType);

      if (isMapServicesVisible) {
        setMapService(EMapService.Routes);
      }
    },
    [closeOnboarding, isMapServicesVisible, regularInfrastructureType, setMapService, startRouting],
  );

  const handleRouteBuildingSuccess = useCallback(
    (params: IRoutingBuildSuccessParams) => {
      if (params.routes.length) {
        const infrastructurePoint = infrastructurePointRef.current;

        const isObjectPoint = !!infrastructurePoint && checkIsInfrastructureRoute(infrastructurePoint, params);
        const isFutureObject = infrastructurePoint
          ? 'constructionYearFrom' in infrastructurePoint || 'overhaulYears' in infrastructurePoint
          : false;

        if (isObjectPoint) {
          infrastructurePointRef.current = null;
        }

        trackBuildRouteSuccess({
          searchResult: params,
          objectType: regularInfrastructureType,
          isObjectPoint,
          isFutureObject,
          hasFutureInfrastructure,
        });
      }
    },
    [hasFutureInfrastructure, regularInfrastructureType],
  );

  const handleBalloonRouteButtonClick = useCallback(
    (item: TInfrastructureItem | TTAInfrastructureItem) => {
      if (isTransportAccessibilityInfrastructureItem(item)) {
        setTARouting(item as TTAInfrastructureItem);
      } else {
        const routeControlViewType = isMapServicesVisible
          ? ERouteControlViewType.stickyLeft
          : ERouteControlViewType.default;

        infrastructurePointRef.current = item;
        startBuildingRoute(item, routeControlViewType);
      }
    },
    [isMapServicesVisible, startBuildingRoute, setTARouting],
  );

  return {
    resetTARouting,
    startBuildingRoute,
    handleRouteBuildingSuccess,
    handleBalloonRouteButtonClick,
  };
}
